<template>
  <div>

    <h5 class="mb-4">Vielen Dank für Ihre Angaben</h5>
    <h5>Hier eine Zusammenfassung Ihrer Buchung:</h5>

    <div class="insurance-data">

      <!-- Zeitslot Section -->
      <div class="mb-3">
        <p style="font-weight:bold">Ihr gewählter Zeitslot:</p>
        <div class="selected-timeslot">
          <span v-if="hasValidTimeslot">
            - Abgabe: <strong>{{ getStartTime() }}</strong><br />
            - Abholung: <strong>{{ getEndTime() }}</strong><br />
          </span>
          <span v-else class="no-data">Kein Zeitslot ausgewählt</span>
        </div>
      </div>

      <!-- Services Section -->
      <div class="mb-3">
        <p style="font-weight:bold">Ihre gewählten Leistungen:</p>
        <div v-if="formdata.selectedServices && formdata.selectedServices.length > 0">
          <div
              v-for="service in formdata.selectedServices"
              :key="service.key"
              class="service-item"
          >
            - {{ service.description || 'Unbenannte Leistung' }}
          </div>
        </div>
        <div v-else class="no-data">Keine Leistungen ausgewählt</div>
      </div>

      <!-- Additional Options Section -->
      <div>
        <p style="font-weight:bold">Zusätzliche Optionen:</p>
        <div v-if="formdata.selectedAdditionalOptions && formdata.selectedAdditionalOptions.length > 0">
          <div
              v-for="(option, index) in formdata.selectedAdditionalOptions"
              :key="index"
              class="option-item"
          >
            - {{ option.description || 'Unbenannte Option' }}
          </div>
        </div>
        <div v-else class="no-data">Keine zusätzlichen Optionen ausgewählt</div>
      </div>

    </div>

    <!-- Guest Booking Buttons -->
    <div class="d-sm-flex justify-content-between" v-if="!identityToken">
      <button @click="openLoginWindow" class="btn btn-primary my-1 mr-1">
        <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
          <path fill-rule="evenodd" d="M8 10V7a4 4 0 1 1 8 0v3h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1Zm2-3a2 2 0 1 1 4 0v3h-4V7Zm2 6a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z" clip-rule="evenodd"/>
        </svg>
        Weiter mit Anmeldung
      </button>
      <button @click="goToServiceForm" class="btn btn-primary my-1 mr-1">
        <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" stroke-width="2" d="M7 17v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3Zm8-9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
        </svg>
        Weiter als Gast
      </button>
    </div>

    <!-- Checkout for logged in users -->
    <div v-if="identityToken">

      <KSRShopAccount
        :studioId="studioId"
        :identityToken="identityToken"
        @account-status-changed="onAccountStatusChanged"
      />

      <template v-if="accountStatus && accountStatus !== 10">
        <div class="form-row">
          <div class="col-md-12">
            <p><strong><u>Bitte ergänzen Sie noch folgende Daten zu Ihrem Fahrzeug:</u></strong></p>
          </div>
        </div>

        <KSRSelectVehicle @setvehicle="setVehicle" :kit="identityToken" />

        <div class="invalid-feedback d-block invalid-vehicle" v-if="formdata_errors.includes('licenseplate')">
          Bitte wählen Sie ein Fahrzeug aus.
        </div>

        <!-- Fahrzeugschein Upload -->
        <KSRFahrzeugscheinUpload
            ref="fahrzeugscheinUpload"
            :required="content && content.setting_service_registration_required"
            :has-error="formdata_errors.includes('registration_document')"
            @update="onFahrzeugscheinUpdate"
        />

        <div class="form-group">
          <label class="form-label">Bemerkung:</label>
          <textarea name="description" v-model="formdata.comment" class="form-control"></textarea>
          <p class="small text-muted">Platz für zusätzliche Infos.</p>
        </div>

        <button @click="onBookServiceAppointment" class="btn btn-primary my-1 mr-1">
          <div class="spinner-border text-white mr-2" role="status" v-if="loading">
            <span class="sr-only">Loading...</span>
          </div>
          Jetzt buchen
        </button>

        <div class="mt-2 text-danger"><small>* = Erforderlich</small></div>

      </template>

    </div>

  </div>
</template>

<script>
import KSRSelectVehicle from "./KSRSelectVehicle.vue";
import KSRShopAccount from "./KSRShopAccount.vue";
import KSRFahrzeugscheinUpload from "./KSRFahrzeugscheinUpload.vue";

export default {
  name: 'KSRServiceCheckout',
  components: {KSRSelectVehicle, KSRShopAccount, KSRFahrzeugscheinUpload},
  data() {
    return {
      loading: false,
      studioId: false,
      formdata: {
        // Vehicle Information
        licenseplate: '',
        vin: '',
        brand: '',
        model: '',
        ez: '',

        // Service Selection
        selectedArea: '',
        selectedGroup: '',
        selectedService: '',
        selectedServices: [],
        selectedAdditionalOptions: [],

        // Appointment & Options
        selectedAppointmentSlot: null,
        currentDay: null,
        optionInKey: '',
        optionOutKey: '',

        // Additional Information
        description: '',
        infos: '',
        comment: '',

        // Fahrzeugschein files
        files_registration: []
      },
      formdata_errors: [],
      identityToken: false,
      accountStatus: null
    }
  },
  created() {

    this.studioId = this.$parent.studioId;

    // Load data from localStorage
    this.loadFromLocalStorage();

    // Check for identityToken
    this.identityToken = sessionStorage.getItem('identityToken');
  },
  computed: {
    content() {
      return this.$parent.content || {};
    },
    hasValidTimeslot() {
      return this.formdata.selectedAppointmentSlot?.appointmentSlots?.length >= 2;
    }
  },
  methods: {
    onAccountStatusChanged(status) {
      this.accountStatus = status;
    },
    onFahrzeugscheinUpdate(files) {
      this.formdata.files_registration = files;
    },
    loadFromLocalStorage() {
      try {
        const savedData = localStorage.getItem('formdata');
        if (savedData) {
          const parsedData = JSON.parse(savedData);

          if (this.isValidFormData(parsedData)) {
            this.formdata = {
              ...this.formdata, // Keep defaults
              ...this.sanitizeFormData(parsedData)
            };
          }
        }
      } catch (error) {
        console.error('Error loading from localStorage:', error);
        localStorage.removeItem('formdata');
      }
    },

    isValidFormData(data) {
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return false;
      }

      const schema = {
        selectedArea: 'string',
        selectedGroup: 'string',
        selectedService: 'string',
        selectedServices: 'array',
        selectedAdditionalOptions: 'array',
        optionInKey: 'string',
        optionOutKey: 'string',
        selectedAppointmentSlot: 'object',
        comment: 'string',
        currentDay: 'string',
        licenseplate: 'string',
        ez: 'string',
        brand: 'string',
        model: 'string',
        vin: 'string'
      };

      for (const [key, expectedType] of Object.entries(schema)) {
        if (data.hasOwnProperty(key)) {
          const actualType = Array.isArray(data[key]) ? 'array' :
              data[key] === null ? 'null' : typeof data[key];

          if (expectedType !== actualType && !(expectedType === 'object' && actualType === 'null')) {
            console.warn(`Invalid type for ${key}: expected ${expectedType}, got ${actualType}`);
            return false;
          }
        }
      }

      return true;
    },

    sanitizeFormData(data) {
      const sanitized = {};

      const stringFields = ['selectedArea', 'selectedGroup', 'selectedService',
        'optionInKey', 'optionOutKey', 'comment', 'licenseplate',
        'ez', 'brand', 'model', 'vin'];

      stringFields.forEach(field => {
        if (data[field] && typeof data[field] === 'string') {
          sanitized[field] = data[field].trim().substring(0, 500);
        }
      });

      if (data.currentDay && typeof data.currentDay === 'string') {
        try {
          const date = new Date(data.currentDay);
          if (!isNaN(date.getTime())) {
            sanitized.currentDay = data.currentDay;
          }
        } catch (e) {
          console.warn('Invalid date format for currentDay');
        }
      }

      if (Array.isArray(data.selectedServices)) {
        sanitized.selectedServices = data.selectedServices.filter(service =>
            service && typeof service === 'object' && service.key
        );
      }

      if (Array.isArray(data.selectedAdditionalOptions)) {
        sanitized.selectedAdditionalOptions = data.selectedAdditionalOptions.filter(option =>
            option && typeof option === 'object'
        );
      }

      if (data.selectedAppointmentSlot && typeof data.selectedAppointmentSlot === 'object') {
        if (this.isValidAppointmentSlot(data.selectedAppointmentSlot)) {
          sanitized.selectedAppointmentSlot = data.selectedAppointmentSlot;
        }
      }

      return sanitized;
    },

    isValidAppointmentSlot(slot) {
      return slot &&
          Array.isArray(slot.appointmentSlots) &&
          Array.isArray(slot.serviceSlots) &&
          Array.isArray(slot.missingServiceOptions);
    },
    formatToMunichTime(isoString) {
      if (!isoString) return 'Nicht verfügbar';

      try {
        const date = new Date(isoString);
        return date.toLocaleString('de-DE', {
          timeZone: 'Europe/Berlin',
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Ungültiges Datum';
      }
    },

    formatTimeslot() {
      const slots = this.formdata.selectedAppointmentSlot?.appointmentSlots;
      if (!slots || slots.length < 2) return 'Kein Zeitslot verfügbar';

      const startTime = this.formatToMunichTime(slots[0].start);
      const endTime = this.formatToMunichTime(slots[1].start);

      return `Abgabe: <strong>${startTime}</strong><br /> Abholung: <strong>${endTime}</strong>`;
    },
    getStartTime() {
      const slots = this.formdata.selectedAppointmentSlot?.appointmentSlots;
      if (!slots || slots.length < 2) return 'Kein Zeitslot verfügbar';
      return this.formatToMunichTime(slots[0].start);
    },
    getEndTime() {
      const slots = this.formdata.selectedAppointmentSlot?.appointmentSlots;
      if (!slots || slots.length < 2) return 'Kein Zeitslot verfügbar';
      return this.formatToMunichTime(slots[1].start);
    },
    goToServiceForm() {
      // Navigate to guest appointment
      this.$router.push('/service-form/create');
    },
    openLoginWindow(section = 'kl') {
      var w = 400;
      var h = 500;
      var left = (screen.width/2)-(w/2);
      var top = (screen.height/2)-(h/2);
      var addSectionToUrl = '';
      if(section == 'service') {
        var addSectionToUrl = '&section=service';
      }
      //https://pre.booklyn.io/s/ksrdemo.booklyn.io/ksrlogin/1
      if(window.location.href.indexOf("/s/") != -1) {
        //window.open('/s/pre.booklyn.io/ksrlogin/' + this.studioId + addSectionToUrl, 'ksrlogin', 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width='+w+', height='+h+', top='+top+', left='+left);
        window.open('/s/pre.booklyn.io/ksrlogin/' + this.studioId + '?cookieless' + addSectionToUrl, 'ksrlogin', 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width='+w+', height='+h+', top='+top+', left='+left);
      }
      else {
        //window.open('/ksrlogin/' + this.studioId + addSectionToUrl, 'ksrlogin', 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width='+w+', height='+h+', top='+top+', left='+left);
        window.open('/ksrlogin/' + this.studioId + '?cookieless' + addSectionToUrl, 'ksrlogin', 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width='+w+', height='+h+', top='+top+', left='+left);
      }
    },
    setVehicle(vehicle) {

      if(!vehicle) {
        this.formdata.brand = '';
        this.formdata.model = '';
        this.formdata.ez = '';
        this.formdata.licenseplate = '';
        this.formdata.vin = '';
        this.formdata.vehicleId = false
        /*this.formdata_offer.brand = '';
        this.formdata_offer.model = '';
        this.formdata_offer.ez = '';
        this.formdata_offer.licenseplate = '';
        this.formdata_offer.vin = '';
        this.formdata_offer.vehicleId = false;*/
      }

      this.formdata.brand = vehicle.brand;
      this.formdata.model = vehicle.model;
      if(vehicle.registration_date) {
        this.formdata.ez = moment(vehicle.registration_date).format('YYYY-MM-DD');
      }
      this.formdata.licenseplate = vehicle.licenseplate;
      this.formdata.vin = vehicle.vin;
      this.formdata.vehicleId = vehicle.id;

      /*this.formdata_offer.brand = vehicle.brand;
      this.formdata_offer.model = vehicle.model;
      this.formdata_offer.ez = moment(vehicle.registration_date).format('YYYY-MM-DD');
      this.formdata_offer.licenseplate = vehicle.licenseplate;
      this.formdata_offer.vin = vehicle.vin;
      this.formdata_offer.vehicleId = vehicle.id;*/

    },
    fileAdded(file) {
      // Construct your file object to render in the UI
      let attachment = {};
      attachment._id = file.upload.uuid;
      attachment.title = file.name;
      attachment.type = "file";
      attachment.extension = "." + file.type.split("/")[1];
      attachment.content = "File Upload by Select or Drop";
      attachment.thumb = file.dataURL;
      attachment.thumb_list = file.dataURL;
      attachment.isLoading = true;
      attachment.progress = null;
      attachment.size = file.size;
      this.tempAttachments = [...this.tempAttachments, attachment];
    },
    sendingFiles(files, xhr, formData) {
      let uuids = [];
      for(var k = 0; k < files.length; k++) {
        uuids.push(files[k].upload.uuid);
      }
      formData.append('uuids', uuids);
      formData.append('type', 'viewing-appointment');
    },
    // function where we get the upload progress
    uploadProgress(file, progress, bytesSent) {
      this.tempAttachments.map(attachment => {
        if (attachment.title === file.name) {
          attachment.progress = `${Math.floor(progress)}`;
        }
      });
    },
    // called on successful upload of a file
    success(file, response) {
      for(var i = 0; i < file.length; i++) {
        for(var k = 0; k < this.tempAttachments.length; k++) {
          if(this.tempAttachments[k]._id == file[i].upload.uuid) {
            //console.log("update");
            this.tempAttachments[k].title = response[i];
            this.tempAttachments[k].thumb = file[i].dataURL;
            this.tempAttachments[k].thumb_list = file[i].dataURL;
          }
        }
      }
    },
    async onBookServiceAppointment() {
      // Reset and validate
      this.formdata_errors = [];

      // Validate Fahrzeugschein if required
      if (this.content && this.content.setting_service_registration_required && this.formdata.files_registration.length === 0) {
        this.formdata_errors.push('registration_document');
      }

      // Handle validation errors
      if (this.formdata_errors.length) {
        setTimeout(() => {
          document.querySelector('.invalid-feedback')?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 200);
        return;
      }

      // Submit booking
      this.loading = true;

      console.log('Prepare booking data');

      try {
        await axios.post('/api/ksr/'+this.studioId+'/book-service-slot/?identityToken='+this.identityToken, this.formdata);
        setTimeout(function() {
          var element = document.querySelector("#booking-container");
          element.scrollIntoView();
        }, 200);
        this.$router.push('/thank-you');
      } catch (error) {
        console.error('Kit booking failed:', error);
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      // Go back using hash navigation (since router might not be available)
      window.location.hash = '';
      // Alternative: this.$router.go(-1) if router is available
    }
  }
}
</script>